/* Prices and availability come from the current Liquid product objects. */
(() => {
  const escape = value => String(value ?? '').replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
  const pieces = config => config.products.filter(p => p.available && p.variants.some(v => v.available));
  const counts = config => Array.from({length: Math.max(0, Math.min(4, pieces(config).length) - 1)}, (_, i) => i + 2);
  const firstVariant = p => p.variants.find(v => v.available);
  const category = p => /earrings/i.test(p.title) ? 'Earrings' : /necklace/i.test(p.title) ? 'Necklace' : /bracelet/i.test(p.title) ? 'Bracelet' : /ring/i.test(p.title) ? 'Ring' : p.title.split(' - ')[0];
  const subtotal = products => products.reduce((sum, p) => sum + firstVariant(p).price, 0);
  const cartItems = selected => {
    if (selected.length < 2 || selected.length > 4 || selected.some(x => !x.variant?.available || !x.product.variants.some(v => v.id === x.variant.id))) throw new Error('Please select an available option for every piece.');
    return selected.map(x => ({id:x.variant.id, quantity:1}));
  };
  // Export only under Node for focused cart/availability tests.
  if (typeof module !== 'undefined') { module.exports = {pieces, counts, subtotal, category, cartItems}; return; }
  if (customElements.get('sw-jewelry-sets')) return;
  customElements.define('sw-jewelry-sets', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.configs = JSON.parse(this.querySelector('[data-set-data]').textContent).filter(c => counts(c).length);
      this.families = [...new Set(this.configs.map(c => c.family))];
      this.states = new Map();
      this.filter = 'all';
      this.busy = false;
      this.dialog = this.querySelector('dialog');
      this.money = value => new Intl.NumberFormat(this.dataset.locale || 'en', {style:'currency', currency:this.dataset.currency || 'USD'}).format(value / 100);
      this.families.forEach(f => {
        const config = this.configs.find(c => c.family === f);
        this.states.set(f, {config, count: counts(config).includes(3) ? 3 : 2, variants:{}});
      });
      this.addEventListener('click', e => this.click(e));
      this.addEventListener('change', e => {
        if (!e.target.matches('[data-variant]')) return;
        const s = this.states.get(this.activeFamily);
        s.variants[e.target.dataset.variant] = e.target.value;
        this.updateTotal();
      });
      this.dialog.addEventListener('close', () => {
        document.body.style.overflow = this.previousOverflow || '';
        this.renderCards();
        this.querySelector(`[data-open="${CSS.escape(this.activeFamily)}"]`)?.focus();
      });
      this.dialog.addEventListener('cancel', e => { if (this.busy) e.preventDefault(); });
      this.renderCards();
    }
    familyConfigs(f) { return this.configs.filter(c => c.family === f); }
    visibleState(f) {
      const state = this.states.get(f);
      if (this.filter === 'all') return state;
      const n = Number(this.filter);
      if (!counts(state.config).includes(n)) {
        const match = this.familyConfigs(f).find(c => counts(c).includes(n));
        if (!match) return null;
        state.config = match;
        state.variants = {};
      }
      state.count = n;
      return state;
    }
    renderCards() {
      const entries = this.families.map(f => [f, this.visibleState(f)]).filter(([,s]) => s);
      this.querySelector('[data-result-count]').textContent = `${entries.length} collections`;
      this.querySelector('[data-set-grid]').innerHTML = entries.map(([family, state], i) => {
        const config = state.config, products = pieces(config).slice(0, state.count);
        return `<article class="sw-set-card" aria-label="${escape(family)} set">
          <div class="sw-set-collage" data-pieces="${products.length}">${products.map(p => `<img src="${escape(p.image)}" alt="${escape(p.title)}" width="500" height="500" loading="lazy">`).join('')}</div>
          <div class="sw-set-details">
            <div class="sw-set-card-top"><h3>${escape(family)}</h3><span class="sw-set-index">${String(i+1).padStart(2,'0')}</span></div>
            <p class="sw-set-subtitle">${escape(config.gem)} · ${escape(config.metal)} tone</p>
            <div class="sw-set-counts" role="group" aria-label="${escape(family)} set size">${counts(config).map(n => `<button type="button" data-card-count="${n}" data-family="${escape(family)}" aria-pressed="${state.count === n}">${n} pieces</button>`).join('')}</div>
            <p class="sw-set-includes">${products.map(category).join(' + ')}</p>
            <div class="sw-set-card-bottom"><div><strong>${this.money(subtotal(products))}</strong><small>Before discounts</small></div><button type="button" class="sw-sets-primary" data-open="${escape(family)}">Choose your set ↗</button></div>
          </div></article>`;
      }).join('') || '<p>No complete sets are available in this selection right now.</p>';
    }
    click(e) {
      const button = e.target.closest('button');
      if (!button || !this.contains(button) || this.busy) return;
      const d = button.dataset;
      if (d.filter) {
        this.filter = d.filter;
        this.querySelectorAll('[data-filter]').forEach(b => b.setAttribute('aria-pressed', String(b === button)));
        this.renderCards();
      } else if (d.cardCount) {
        this.filter = 'all';
        this.querySelectorAll('[data-filter]').forEach(b => b.setAttribute('aria-pressed', String(b.dataset.filter === 'all')));
        this.states.get(d.family).count = Number(d.cardCount);
        this.renderCards();
        this.querySelector(`[data-card-count="${d.cardCount}"][data-family="${CSS.escape(d.family)}"]`)?.focus();
      } else if (d.open) {
        this.activeFamily = d.open;
        this.renderDrawer();
        this.previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        this.dialog.showModal();
      } else if ('close' in d) {
        this.dialog.close();
      } else if (d.metal || d.gem) {
        const s = this.states.get(this.activeFamily);
        const options = this.familyConfigs(this.activeFamily);
        const next = d.metal ? options.find(c => c.metal === d.metal && c.gem === s.config.gem) || options.find(c => c.metal === d.metal) : options.find(c => c.metal === s.config.metal && c.gem === d.gem);
        if (!next) return;
        s.config = next;
        s.count = Math.min(s.count, pieces(next).length);
        this.renderDrawer();
        this.dialog.querySelector(d.metal ? `[data-metal="${CSS.escape(d.metal)}"]` : `[data-gem="${CSS.escape(d.gem)}"]`)?.focus();
      } else if (d.drawerCount) {
        this.filter = 'all';
        this.querySelectorAll('[data-filter]').forEach(b => b.setAttribute('aria-pressed', String(b.dataset.filter === 'all')));
        this.states.get(this.activeFamily).count = Number(d.drawerCount);
        this.renderDrawer();
        this.dialog.querySelector(`[data-drawer-count="${d.drawerCount}"]`)?.focus();
      } else if ('add' in d) this.addSet();
    }
    selected() {
      const s = this.states.get(this.activeFamily);
      return pieces(s.config).slice(0, s.count).map(p => {
        const explicit = p.variants.find(v => String(v.id) === String(s.variants[p.id]) && v.available);
        return {product:p, variant:explicit || (p.variants.length === 1 ? firstVariant(p) : null)};
      });
    }
    renderDrawer() {
      const s = this.states.get(this.activeFamily), config = s.config;
      const configs = this.familyConfigs(this.activeFamily);
      const metals = [...new Set(configs.map(c => c.metal))];
      const gems = [...new Set(configs.filter(c => c.metal === config.metal).map(c => c.gem))];
      const products = pieces(config).slice(0, s.count);
      this.querySelector('[data-drawer-body]').innerHTML = `<h2 id="sw-sets-dialog-title">${escape(this.activeFamily)}</h2><p class="sw-set-note">${escape(config.note)}</p>
        <fieldset class="sw-set-field"><legend>METAL COLOR</legend><div class="sw-set-options">${metals.map(m => `<button type="button" data-metal="${escape(m)}" aria-pressed="${m === config.metal}"><span class="sw-set-metal-dot" data-metal="${escape(m)}" aria-hidden="true"></span>${escape(m)}</button>`).join('')}</div></fieldset>
        <fieldset class="sw-set-field"><legend>GEMSTONE / COLOR</legend><div class="sw-set-options">${gems.map(g => `<button type="button" data-gem="${escape(g)}" aria-pressed="${g === config.gem}">${escape(g)}</button>`).join('')}</div></fieldset>
        <fieldset class="sw-set-field"><legend>YOUR SET</legend><div class="sw-set-options">${counts(config).map(n => `<button type="button" data-drawer-count="${n}" aria-pressed="${n === s.count}">${n} pieces</button>`).join('')}</div></fieldset>
        <p class="sw-sets-eyebrow">INCLUDED IN YOUR SET</p>
        ${products.map(p => `<div class="sw-set-line"><img src="${escape(p.image)}" alt="${escape(p.title)}" width="65" height="75"><div>
        <a href="${escape(p.url)}" target="_blank" rel="noopener">${escape(p.title)}</a><span class="sw-set-line-price">${this.money((p.variants.find(v => String(v.id) === String(s.variants[p.id]) && v.available) || firstVariant(p)).price)}</span>
        ${p.variants.length > 1 ? `<label>${escape(p.options.join(' / '))}<select data-variant="${p.id}" aria-label="${escape(p.title)} size"><option value="">Select ${escape(p.options.join(' / ').toLowerCase())}</option>${p.variants.map(v => `<option value="${v.id}" ${v.available?'':'disabled'} ${String(s.variants[p.id]) === String(v.id)?'selected':''}>${escape(v.title)}${v.available?'':' — Sold out'}</option>`).join('')}</select></label>` : '<small>One size</small>'}
        </div></div>`).join('')}`;
      this.querySelector('[data-error]').hidden = true;
      this.querySelector('[data-cart-check]').hidden = true;
      this.updateTotal();
    }
    updateTotal() {
      const selected = this.selected();
      const total = selected.reduce((sum, x) => sum + (x.variant || firstVariant(x.product)).price, 0);
      this.querySelector('[data-total]').textContent = this.money(total);
      const add = this.querySelector('[data-add]');
      add.textContent = selected.every(x => x.variant) ? `Add ${selected.length}-piece set to cart` : 'Select your size to continue';
      add.disabled = this.busy || !selected.every(x => x.variant);
      this.querySelectorAll('[data-variant]').forEach(select => {
        const item = selected.find(x => String(x.product.id) === select.dataset.variant);
        select.closest('.sw-set-line').querySelector('.sw-set-line-price').textContent = this.money((item.variant || firstVariant(item.product)).price);
      });
    }
    async addSet() {
      const selected = this.selected();
      if (this.busy || selected.length < 2 || !selected.every(x => x.variant)) return;
      this.busy = true;
      this.dialog.querySelectorAll('button,select').forEach(el => el.disabled = true);
      const add = this.querySelector('[data-add]'), error = this.querySelector('[data-error]');
      add.textContent = 'Adding your set…';
      error.hidden = true;
      try {
        const cartHelper = window.Alpine?.store('xCartHelper');
        const sections = cartHelper?.getSectionsToRender?.().map(section => section.id) || [];
        const body = {items:cartItems(selected)};
        if (sections.length) {
          body.sections = sections;
          body.sections_url = window.location.pathname;
        }
        window.updatingCart = true;
        const response = await fetch(`${this.dataset.root || '/'}cart/add.js`, {
          method:'POST', headers:{'Content-Type':'application/json', 'Accept':'application/json'},
          body:JSON.stringify(body)
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || data.message || 'This combination could not be added.');
        const miniCart = window.Alpine?.store('xMiniCart');
        if (!cartHelper || !miniCart) throw new Error('Your set was added, but the cart panel could not open. Please use the cart icon to review it.');
        this.busy = false;
        this.dialog.close();
        miniCart.reLoad();
        miniCart.openCart();
      } catch (err) {
        error.textContent = `${err.message} Please check your cart before trying again.`;
        error.hidden = false;
        this.querySelector('[data-cart-check]').hidden = false;
        this.busy = false;
        this.dialog.querySelectorAll('button,select').forEach(el => el.disabled = false);
        this.updateTotal();
      } finally {
        window.updatingCart = false;
      }
    }
  });
})();
